import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { servicesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, MapPin, DollarSign, Star } from 'lucide-react';
import RatingModal from '../components/RatingModal';

const ServiceHistory = () => {
  const navigate = useNavigate();
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const data = await servicesAPI.getCustomerServiceRequests();
      setServiceHistory(data.service_requests);
    } catch (error) {
      console.error('Error fetching service history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRateService = (service) => {
    setSelectedService(service);
    setRatingModalOpen(true);
  };

  const handleRatingSubmitted = () => {
    fetchHistory(); // Refresh to show updated rating status
  };

  return (
    <div className="min-h-screen bg-wheely-navy-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-wheely-navy-950 to-wheely-navy-900 border-b border-wheely-orange/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/customer/dashboard')}
            className="text-wheely-orange hover:text-wheely-orange-light hover:bg-wheely-orange/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-3xl font-bold text-white mb-8">Service History</h2>

        <div className="space-y-4">
          {serviceHistory.map((service) => (
            <Card key={service.id} style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }} className="hover:border-wheely-orange/30/50 transition-colors">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white text-xl mb-2">{service.service}</CardTitle>
                    <Badge style={{
                      background: service.status === 'completed' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(235, 152, 104, 0.2)',
                      color: service.status === 'completed' ? '#10b981' : '#EB9868'
                    }}>
                      {service.status}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-sm">Total Cost</p>
                    <p className="text-wheely-orange-light font-bold text-2xl">£{service.price?.toFixed(2) || '0.00'}</p>
                    {service.tip_amount && (
                      <p className="text-green-400 text-sm">+ £{service.tip_amount.toFixed(2)} tip</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>{service.created_at ? new Date(service.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span>{service.location}</span>
                </div>
                {service.driver_name && (
                  <div className="flex items-center gap-3 text-gray-400">
                    <span className="text-sm">Driver: <span className="text-white font-semibold">{service.driver_name}</span></span>
                  </div>
                )}
                
                {service.status === 'cancelled' && (
                  <p className="text-gray-400 text-sm">
                    Cancelled{service.refund_amount > 0 ? ` · £${Number(service.refund_amount).toFixed(2)} refunded` : ''}
                  </p>
                )}

                {['pending', 'quoted', 'accepted', 'in_progress'].includes(service.status) && (
                  <Button
                    onClick={() => navigate('/customer/tracking', {
                      state: { service: { name: service.service }, serviceRequestId: service.id, estimatedCost: service.price }
                    })}
                    style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
                    className="w-full mt-4"
                  >
                    {service.status === 'accepted' && !service.paid ? 'Pay or cancel' : 'View request'}
                  </Button>
                )}

                {/* Rating Button for Completed Services */}
                {service.status === 'completed' && !service.rated_by_customer && (
                  <Button
                    onClick={() => handleRateService(service)}
                    style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
                    className="w-full mt-4"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Rate This Service
                  </Button>
                )}
                
                {service.rated_by_customer && (
                  <div className="bg-wheely-orange/20/10 border border-wheely-orange/30/30 rounded-lg p-3 mt-4">
                    <p className="text-wheely-orange-light text-sm">✓ You rated this service</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {serviceHistory.length === 0 && !loading && (
          <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
            <CardContent className="py-12 text-center">
              <p className="text-gray-300 text-lg">No service history yet</p>
              <Button
                onClick={() => navigate('/customer/dashboard')}
                style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
                className="mt-4"
              >
                Request Your First Service
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Rating Modal */}
        {selectedService && (
          <RatingModal
            isOpen={ratingModalOpen}
            onClose={() => setRatingModalOpen(false)}
            serviceRequest={selectedService}
            userRole="customer"
            onRatingSubmitted={handleRatingSubmitted}
          />
        )}
      </div>
    </div>
  );
};

export default ServiceHistory;